import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  HeartIcon,
  ArrowLeft,
  InstagramIconOutline, 
  FacebookIconOutline, 
  TwitterIconOutline, 
  LinkedInIconOutline,
  GroupMessage
} from '../../../public/icons';
import { book, camera, writing, Gift } from '../../../public/images/blog';
import publicBlogService from '../../../services/publicBlogService';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { useToast } from '../../../context/ToastContext';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openModal } = useModal();
  const toast = useToast();

  const [blog, setBlog] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({
    name: '',
    email: '',
    text: ''
  });

  useEffect(() => {
    fetchBlog();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const response = await publicBlogService.getBlogBySlug(slug);
      setBlog(response.data.blog);
      setRelatedArticles(response.data.relatedArticles);
    } catch (err) {
      console.error('Error fetching blog:', err);
      toast.error('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please log in to like posts');
      openModal('signin');
      return;
    }

    try {
      const response = await publicBlogService.likeBlog(blog._id);
      setBlog(prev => ({ ...prev, likes: response.likes }));
      toast.success('Post liked!');
    } catch (err) {
      console.error('Error liking blog:', err);
      toast.error('Failed to like post');
    }
  };

  const handleShare = async (platform) => {
    if (!isAuthenticated) {
      toast.error('Please log in to share posts');
      openModal('signin');
      return;
    }

    try {
      // Track share
      await publicBlogService.trackShare(blog._id, platform);
      
      // Get share URL
      const url = window.location.href;
      const title = blog.title;
      
      // Open share dialog
      let shareUrl = '';
      switch (platform) {
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
          break;
        case 'twitter':
          shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
          break;
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
          break;
        case 'instagram':
          // Instagram doesn't have direct share URL, copy to clipboard
          navigator.clipboard.writeText(url);
          toast.success('Link copied to clipboard!');
          return;
      }
      
      if (shareUrl) {
        window.open(shareUrl, '_blank', 'width=600,height=400');
      }
      
      toast.success(`Shared on ${platform}!`);
    } catch (err) {
      console.error('Error sharing:', err);
      toast.error('Failed to share');
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please log in to comment');
      openModal('signin');
      return;
    }
    
    if (!commentForm.name || !commentForm.email || !commentForm.text) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await publicBlogService.addComment(blog._id, commentForm);
      toast.success('Comment submitted for approval!');
      setCommentForm({ name: '', email: '', text: '' });
    } catch (err) {
      console.error('Error submitting comment:', err);
      toast.error('Failed to submit comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-basic pt-12 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55000C]"></div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-basic pt-12">
        <div className="container mx-auto px-4 max-w-7xl text-center">
          <h1 className="text-2xl font-bold text-[#1C2A46]">Blog post not found</h1>
          <Link to="/blog" className="mt-4 inline-block text-[#D2A146] hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-basic pt-12">
      {/* Blog Post Header */}
      <section className="flex justify-start items-start">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Category */}
          <div className="mb-4 flex justify-start items-start">
            <span className="px-4 py-2 bg-[#55000C] text-[#FCF7EE] text-sm font-normal">
              {blog.category}
            </span>
          </div>
          
          {/* Title */}
          <h1 className="text-4xl md:text-4xl font-bold text-[#1C2A46] mb-2.5 text-left">
            {blog.title}
          </h1>
          
          {/* Date & Read Time */}
          <div className="flex items-center gap-8 text-[#6B635D] text-base mb-8">
            <span className="flex items-center gap-2">
              <CalendarIcon size={18} />
              {new Date(blog.createdAt).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
              })}
            </span>
            <span className="flex items-center gap-2">
              <ClockIcon size={18} />
              {blog.readTime}
            </span>
          </div>
          
          <div className='flex justify-between items-center mb-10'>
            <div className='flex gap-5'>
              <div className="flex items-center gap-2 border-t border-basic">
                <button className="flex items-center gap-2 text-[#6B635D] border p-1.5">
                  <GroupMessage size={20} color='#1C2A46' />
                  <span>{blog.comments?.filter(c => c.isApproved).length || 0}</span>
                </button>
              </div> 

              <div className="flex items-center gap-2 border-t border-basic">
                <button 
                  onClick={handleLike}
                  className="flex items-center gap-2 text-[#6B635D] border p-1.5 hover:bg-[#F5F5F5] transition-colors"
                >
                  <HeartIcon size={20} color='#1C2A46' />
                  <span>{blog.likes}</span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-[#6B635D] font-medium">Share:</span>
              <div className="flex gap-3">
                <button 
                  onClick={() => handleShare('facebook')}
                  className="text-[#6B635D] hover:text-[#1C2A46] transition-colors"
                >
                  <FacebookIconOutline color="#6B635D" size={20} className="hover:scale-110 transition-transform" />
                </button>
                <button 
                  onClick={() => handleShare('twitter')}
                  className="text-[#6B635D] hover:text-[#1C2A46] transition-colors"
                >
                  <TwitterIconOutline color="#6B635D" size={20} className="hover:scale-110 transition-transform" />
                </button>
                <button 
                  onClick={() => handleShare('instagram')}
                  className="text-[#6B635D] hover:text-[#1C2A46] transition-colors"
                >
                  <InstagramIconOutline color="#6B635D" size={20} className="hover:scale-110 transition-transform" />
                </button>
                <button 
                  onClick={() => handleShare('linkedin')}
                  className="text-[#6B635D] hover:text-[#1C2A46] transition-colors"
                >
                  <LinkedInIconOutline color="#6B635D" size={20} className="hover:scale-110 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-[#E4E4E4] mb-9"></div>
          
          {/* Hero Image */}
          <div className="mb-9">
            <img
              src={blog.featuredImage}
              alt={blog.title}
              className="w-full"
            />
          </div>
          
          {/* Intro Paragraph */}
          <div className="mb-6 text-left">
            <div 
              className="text-xl font-normal text-[#1C2A46] prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: blog.content.intro }}
            />
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="pb-16">
        <div className="container mx-auto px-4 max-w-7xl text-left">
          {blog.content.sections.map((section, index) => (
            <div key={index} className="mb-6 last:mb-0">
              <h2 className="text-xl font-medium text-[#1C2A46] mb-6">
                {section.title}
              </h2>
              
              <div 
                className="text-[#1C2A46] text-xl font-normal leading-relaxed mb-6 prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: section.text }}
              />
              
              {/* Quote */}
              {section.quote && (
                <div className="border-l-2 border-[#55000C] pl-6 my-8">
                  <p className="text-xl italic text-[#1C2A46] mb-2 max-w-5xl">
                    "{section.quote.text}"
                  </p>
                  {section.quote.author && (
                    <p className="text-[#6B635D]">— {section.quote.author}</p>
                  )}
                </div>
              )}
              
              {/* Tips */}
              {section.tips && (
                <div className="">
                  <h3 className="text-lg font-semibold text-[#1C2A46] mb-4">
                    Tips for Getting Started:
                  </h3>
                  <ul className="space-y-3">
                    {section.tips.map((tip, tipIndex) => (
                      <li key={tipIndex} className="flex items-start">
                        <span className="text-[#1C2A46] mr-3 mt-1">•</span>
                        <span className="text-[#1C2A46] font-normal">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Comments Section */}
      <section className="">
        <div className="container mx-auto max-w-7xl">
          <h2 className="text-3xl font-medium text-left text-[#1C2A46] mb-8">
            Customer Reviews ({blog.comments?.filter(c => c.isApproved).length || 0})
          </h2>

          {/* Divider */}
          <div className="border-t border-[#E4E4E4] my-5"></div>
          
          {/* Comments List */}
          <div className="space-y-8 mb-5">
            {blog.comments?.filter(comment => comment.isApproved).map((comment) => (
              <div key={comment._id} className="">
                <div className="mb-4 text-left">
                  <h3 className="font-medium text-[#1C2A46]">{comment.name}</h3>
                  <span className="text-sm text-[#6B635D] font-normal">
                    {new Date(comment.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                <p className="text-[#151515] text-left font-normal">{comment.text}</p>
              </div>
            ))}
          </div>
          
          {/* Comment Form */}
          <div className="text-left mb-14 max-w-5xl">
            <h3 className="text-xl font-medium text-[#000000] mb-6">Leave a comment</h3>
            
            <form onSubmit={handleCommentSubmit} className="space-y-6">
              <div>
                <input
                  type="text"
                  placeholder="enter your name*"
                  value={commentForm.name}
                  onChange={(e) => setCommentForm({ ...commentForm, name: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E4E4E4] bg-[#F5F5F5] font-light text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D]"
                />
              </div>
              
              <div>
                <input
                  type="email"
                  placeholder="enter your email*"
                  value={commentForm.email}
                  onChange={(e) => setCommentForm({ ...commentForm, email: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E4E4E4] bg-[#F5F5F5] font-light text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D]"
                />
              </div>
              
              <div>
                <textarea
                  rows="4"
                  placeholder="Write your review here.*"
                  value={commentForm.text}
                  onChange={(e) => setCommentForm({ ...commentForm, text: e.target.value })}
                  className="w-full px-4 py-3 border border-[#E4E4E4] bg-[#F5F5F5] font-light text-[#6B635D] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] resize-none"
                />
              </div>
              
              <button
                type="submit"
                className="px-5 py-4 bg-primary text-[#FCF7EE] font-medium hover:bg-opacity-90 transition-colors"
              >
                Post Comment
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-7xl">
            <h2 className="text-2xl font-bold text-[#1C2A46] mb-12">Related Articles</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedArticles.map((post) => (
                <Link 
                  key={post._id} 
                  to={`/blog/${post.slug}`}
                  className="group"
                >
                  <div className="group cursor-pointer">
                    <div className="overflow-hidden mb-4">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between mb-3">
                      <span className="p-1.5 border border-bordercolor text-[#1C2A46] text-xs font-normal">
                        {post.category}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-medium text-[#1C2A46] mb-3 text-left line-clamp-1">
                      {post.title}
                    </h3>
                    <p className="text-[#6B635D] mb-4 text-left text-sm font-light line-clamp-2">
                      {post.excerpt}
                    </p>
                    
                    <div className="">
                      <div className="flex items-center justify-between gap-8 text-[#6B635D] text-sm pb-2.5">
                        <span className="flex items-center gap-2">
                          <CalendarIcon size={14} />
                          {new Date(post.publishDate).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-2">
                          <ClockIcon size={14} />
                          {post.readTime}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;